import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useStore } from '../store/useStore';
import { AdvancedFilters as FiltersType } from '../types';

interface AdvancedFiltersProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters: (filters: FiltersType) => void;
}

export const AdvancedFilters: React.FC<AdvancedFiltersProps> = ({ isOpen, onClose, onApplyFilters }) => {
  const { t } = useTranslation();
  const { advancedFilters, setAdvancedFilters } = useStore();
  const [filters, setFilters] = useState<FiltersType>(advancedFilters);

  useEffect(() => {
    setFilters(advancedFilters);
  }, [advancedFilters, isOpen]);

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault();
    setAdvancedFilters(filters);
    onApplyFilters(filters);
    onClose();
  };

  const handleAgeRangeChange = (index: number, value: number) => {
    const newAgeRange: [number, number] = [...filters.ageRange] as [number, number];
    newAgeRange[index] = value;
    setFilters({ ...filters, ageRange: newAgeRange });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 slide-in">
      <div className="bg-gray-800 rounded-lg max-w-md w-full p-6 fade-in">
        <h3 className="text-xl font-bold text-white mb-4">{t('filters.title')}</h3>

        <form onSubmit={handleApply} className="space-y-4">
          <div>
            <label className="block text-gray-300 mb-2">{t('filters.age_range')}</label>
            <div className="flex space-x-2">
              <input
                type="number"
                min="18"
                max="100"
                value={filters.ageRange[0]}
                onChange={(e) => handleAgeRangeChange(0, parseInt(e.target.value) || 18)}
                className="w-1/2 p-2 rounded bg-gray-700 text-white border border-gray-600 focus:outline-none focus:border-yellow-400 transition-all"
              />
              <span className="self-center text-gray-400">{t('filters.to') || 'até'}</span>
              <input
                type="number"
                min="18"
                max="100"
                value={filters.ageRange[1]}
                onChange={(e) => handleAgeRangeChange(1, parseInt(e.target.value) || 60)}
                className="w-1/2 p-2 rounded bg-gray-700 text-white border border-gray-600 focus:outline-none focus:border-yellow-400 transition-all"
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-300 mb-2">{t('filters.interests') || 'Interesses'}</label>
            <input
              type="text"
              value={filters.interests.join(', ')}
              onChange={(e) => setFilters({ ...filters, interests: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
              className="w-full p-2 rounded bg-gray-700 text-white border border-gray-600 focus:outline-none focus:border-yellow-400 transition-all"
              placeholder={t('filters.interests_placeholder') || 'Ex: música, esportes, tecnologia'}
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">{t('filters.location')}</label>
            <input
              type="text"
              value={filters.location}
              onChange={(e) => setFilters({ ...filters, location: e.target.value })}
              className="w-full p-2 rounded bg-gray-700 text-white border border-gray-600 focus:outline-none focus:border-yellow-400 transition-all"
              placeholder={t('filters.anywhere')}
            />
          </div>

          <div>
            <label className="block text-gray-300 mb-2">{t('filters.language') || 'Idioma'}</label>
            <select
              value={filters.language}
              onChange={(e) => setFilters({ ...filters, language: e.target.value })}
              className="w-full p-2 rounded bg-gray-700 text-white border border-gray-600 focus:outline-none focus:border-yellow-400 transition-all"
            >
              <option value="any">{t('filters.any_language') || 'Qualquer idioma'}</option>
              <option value="portuguese">{t('filters.portuguese') || 'Português'}</option>
              <option value="english">{t('filters.english') || 'Inglês'}</option>
              <option value="spanish">{t('filters.spanish') || 'Espanhol'}</option>
              <option value="french">{t('filters.french') || 'Francês'}</option>
            </select>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="onlineOnly"
              checked={filters.showOnlineOnly}
              onChange={(e) => setFilters({ ...filters, showOnlineOnly: e.target.checked })}
              className="mr-2"
            />
            <label htmlFor="onlineOnly" className="text-gray-300">
              {t('filters.online_only') || 'Mostrar apenas usuários online'}
            </label>
          </div>

          <div className="flex space-x-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2 px-4 bg-gray-600 text-white rounded hover:bg-gray-500 transition-colors transition-all"
            >
              {t('report.cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 py-2 px-4 bg-yellow-500 text-black rounded hover:bg-yellow-400 transition-colors transition-all"
            >
              {t('filters.apply')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};